import type { JSX } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';

import { DesignSystemProvider } from '@trabeate/design-system';
import interLatinHref from '@trabeate/design-system/fonts/inter-latin-variable.woff2?url';

import { BRAND_ICONS } from './config/brand';

// Order is the cascade (change 0006, D5): the design system's tokens, its faces, its component
// rules, then the site's reset. Vite bundles all four into same-origin files, so `style-src
// 'self'` needs no exception.
import '@trabeate/design-system/tokens.css';
import '@trabeate/design-system/fonts.css';
import '@trabeate/design-system/components.css';
import './styles/base.css';

/**
 * The document shell.
 *
 * `<Meta />` and `<Links />` are what make prerendering worth doing: at build time React Router
 * writes each route's meta tags into the emitted HTML, so a crawler that never runs JavaScript
 * still sees the title, description and Open Graph card.
 */
export function Layout({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* Inter's latin subset, from the design system (REQ-LANDING-011). Preloaded because the
            first line of text uses it, and discovering it only when the stylesheet is parsed
            costs a visible reflow. The `?url` import makes this the same hashed, same-origin file
            `fonts.css` names, so the preload cannot drift from the face it warms. */}
        <link
          rel="preload"
          href={interLatinHref}
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />

        {/* Both grounds, light first. The ground itself is chosen by the design system's
            `tokens.css` (REQ-LANDING-012); this lets the browser pick its canvas and form-control
            colours to match before the stylesheet arrives, and light first matches that
            stylesheet's fallback when the device states no preference. */}
        <meta name="color-scheme" content="light dark" />

        {/* The design system's icon, served at the root by `vite.config.ts` (REQ-LANDING-013).
            `sizes="32x32"` on the `.ico` keeps Chromium from preferring it over the SVG. */}
        <link rel="icon" href={`/${BRAND_ICONS.ico}`} sizes="32x32" />
        <link rel="icon" href={`/${BRAND_ICONS.svg}`} type="image/svg+xml" />
        <link rel="apple-touch-icon" href={`/${BRAND_ICONS.appleTouch}`} />

        <Meta />
        <Links />
      </head>
      <body>
        <DesignSystemProvider>{children}</DesignSystemProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function Root(): JSX.Element {
  return <Outlet />;
}

/**
 * Rendered instead of the route when anything throws.
 *
 * Deliberately says nothing specific. An error boundary that prints the exception is a
 * reconnaissance tool on a public page, and a visitor cannot act on a stack trace anyway.
 */
export function ErrorBoundary(): JSX.Element {
  return (
    <main>
      <h1>Something went wrong</h1>
      <p>
        Please try again, or email <a href="mailto:hello@trabeate.com">hello@trabeate.com</a>.
      </p>
    </main>
  );
}
